import ToggleRow from '@components/Customization/ToggleRow';
import { useLocalization } from '@lib/localization';
import { arrayMove } from '@dnd-kit/sortable';
import {
  Add as AddIcon, ArrowDownward as MoveDownIcon, ArrowUpward as MoveUpIcon, DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import {
  ssoAssignableRoles, type SsoAccounts, type SsoAssignableRole, type SsoMappingRule, type SsoTeamAssignment
} from '@store/api/ssoEndpoints';
import { type Team, type TeamRole, teamRoles } from '@store/api/teamEndpoints';
import { type FC } from 'react';

interface Props {
  accounts: SsoAccounts;
  /** Empty until it is read: `teamsLoaded` says when a team missing from it is a deleted one. */
  teams: Team[];
  teamsLoaded: boolean;
  disabled: boolean;
  /** The refusal of `path` as its field says it, if the last save was refused for it. */
  errorAt?: (path: string, options?: { exact?: boolean }) => string | undefined;
  onChange: (accounts: SsoAccounts) => void;
}

/**
 * Who gets in through the identity provider, and as what: whether an account an admin defined
 * may sign in, whether somebody without one is given one, and how accounts are placed: a new
 * one in one fixed place, or by rules on what the provider says about the person, which also
 * place every account the provider manages. The rules are offered whether accounts are created
 * or not, since an account an admin defined can be handed to the provider too.
 */
const SsoAccountsForm: FC<Props> = ({ accounts, teams, teamsLoaded, disabled, errorAt = () => undefined, onChange }) => {
  const { t } = useLocalization();
  // Of a row's field, or of a list as a whole.
  const fieldError = (path: string) => errorAt(path, { exact: true });

  const set = (part: Partial<SsoAccounts>) => onChange({ ...accounts, ...part });

  const roleName = (role: SsoAssignableRole) => t(`User_Role_${role.capitalize()}`);
  const teamRoleName = (role: TeamRole) => t(`Team_Role_${role.toLowerCase().capitalize()}`);

  // A team deleted since the settings were saved is still named in them. It is shown, so that
  // the row can be taken out, and the server passes it over until then.
  const teamOptions = (selected: string | undefined, offered: Team[] = teams) => [
    ...offered.map((team) => <MenuItem key={team._id} value={team._id}>{team.name}</MenuItem>),
    ...(selected && !teams.some((team) => team._id === selected)
      ? [<MenuItem key={selected} value={selected}>{t('ServerConfig_Sso_DeletedTeam')}</MenuItem>]
      : []),
  ];

  const setDefaultTeam = (index: number, part: Partial<SsoTeamAssignment>) => set({
    defaultTeams: accounts.defaultTeams.map((team, at) => (at === index ? { ...team, ...part } : team)),
  });

  const setRule = (index: number, part: Partial<SsoMappingRule>) => set({
    mappings: accounts.mappings.map((rule, at) => (at === index ? { ...rule, ...part } : rule)),
  });

  // Where two rules that hold name one team, the upper one decides the role there. The account's
  // own role is not a matter of order: any rule that holds and gives admin makes it an admin.
  const moveRule = (index: number, to: number) => set({ mappings: arrayMove(accounts.mappings, index, to) });

  const teamName = (teamId: string) => teams.find((team) => team._id === teamId)?.name ?? t('ServerConfig_Sso_DeletedTeam');

  const freeTeam = teams.find((team) => !accounts.defaultTeams.some((assigned) => assigned.teamId === team._id));

  // A deleted team in the part of the settings the form does not show now (the default teams
  // under rules, the rules under the default) would stay there unseen, and turn up again once
  // the part is shown. It is pointed out here and can be taken out. The server lets it be.
  const isKnown = (teamId: string) => teams.some((team) => team._id === teamId);
  const showsDefaultTeams = accounts.assignment === 'default' && accounts.autoCreate;
  const showsRules = accounts.assignment === 'mapping';
  const hidesDeletedTeam = teamsLoaded && (
    (!showsDefaultTeams && accounts.defaultTeams.some((team) => !isKnown(team.teamId)))
    || (!showsRules && accounts.mappings.some((rule) => rule.teamId !== undefined && !isKnown(rule.teamId))));

  // A rule keeps holding without its team: one that gave only the team gives the role `user`,
  // which is what holding it came to while the team was gone.
  const dropHiddenDeletedTeams = () => set({
    ...(showsDefaultTeams ? {} : { defaultTeams: accounts.defaultTeams.filter((team) => isKnown(team.teamId)) }),
    ...(showsRules ? {} : {
      mappings: accounts.mappings.map((rule) => (rule.teamId !== undefined && !isKnown(rule.teamId)
        ? { value: rule.value, role: rule.role ?? 'user' }
        : rule)),
    }),
  });

  return (
    <Stack spacing={1.5}>
      <ToggleRow
        label={t('ServerConfig_Sso_LinkExisting')}
        description={t('ServerConfig_Sso_LinkExisting_Description')}
        checked={accounts.linkExisting}
        onChange={(linkExisting) => set({ linkExisting })}
        disabled={disabled}
      />
      <ToggleRow
        label={t('ServerConfig_Sso_AutoCreate')}
        description={t('ServerConfig_Sso_AutoCreate_Description')}
        checked={accounts.autoCreate}
        onChange={(autoCreate) => set({ autoCreate })}
        disabled={disabled}
      />

      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          select fullWidth size="small"
          label={t('ServerConfig_Sso_Assignment')}
          value={accounts.assignment}
          onChange={(event) => set({ assignment: event.target.value as SsoAccounts['assignment'] })}
          disabled={disabled}
        >
          <MenuItem value="default">{t('ServerConfig_Sso_Assignment_Default')}</MenuItem>
          <MenuItem value="mapping">{t('ServerConfig_Sso_Assignment_Mapping')}</MenuItem>
        </TextField>

        {hidesDeletedTeam && (
          <Alert
            severity="warning"
            action={(
              <Button color="inherit" size="small" onClick={dropHiddenDeletedTeams} disabled={disabled}>
                {t('ServerConfig_Sso_HiddenDeletedTeam_Remove')}
              </Button>
            )}
          >
            {t('ServerConfig_Sso_HiddenDeletedTeam')}
          </Alert>
        )}

        {/* The default places only an account single sign-on creates. */}
        {accounts.assignment === 'default' && accounts.autoCreate && (
          <>
            <TextField
              select fullWidth size="small"
              label={t('Common_Role')}
              value={accounts.defaultRole}
              onChange={(event) => set({ defaultRole: event.target.value as SsoAssignableRole })}
              disabled={disabled}
            >
              {ssoAssignableRoles.map((role) => <MenuItem key={role} value={role}>{roleName(role)}</MenuItem>)}
            </TextField>
            {accounts.defaultRole === 'admin' && <Alert severity="warning">{t('ServerConfig_Sso_DefaultAdmin')}</Alert>}

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('Common_Teams')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ServerConfig_Sso_DefaultTeams_Description')}
              </Typography>
              {fieldError('accounts.defaultTeams') && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  {fieldError('accounts.defaultTeams')}
                </Typography>
              )}
              <Stack spacing={1}>
                {accounts.defaultTeams.map((assigned, index) => (
                  <Stack key={assigned.teamId} direction="row" spacing={1} alignItems="center">
                    <TextField
                      select size="small" sx={{ flex: 1, minWidth: 0 }}
                      value={assigned.teamId}
                      error={Boolean(fieldError(`accounts.defaultTeams.${index}.teamId`))}
                      helperText={fieldError(`accounts.defaultTeams.${index}.teamId`)}
                      onChange={(event) => setDefaultTeam(index, { teamId: event.target.value })}
                      disabled={disabled}
                    >
                      {/* A team is placed once: the ones other rows hold are not offered here. */}
                      {teamOptions(assigned.teamId, teams.filter((team) => team._id === assigned.teamId
                          || !accounts.defaultTeams.some((other) => other.teamId === team._id)))}
                    </TextField>
                    <TextField
                      select size="small" sx={{ width: 150 }}
                      value={assigned.role}
                      error={Boolean(fieldError(`accounts.defaultTeams.${index}.role`))}
                      helperText={fieldError(`accounts.defaultTeams.${index}.role`)}
                      onChange={(event) => setDefaultTeam(index, { role: event.target.value as TeamRole })}
                      disabled={disabled}
                    >
                      {teamRoles.map((role) => <MenuItem key={role} value={role}>{teamRoleName(role)}</MenuItem>)}
                    </TextField>
                    <IconButton
                      size="small"
                      aria-label={t('ServerConfig_Sso_DefaultTeam_Remove', [teamName(assigned.teamId)])}
                      onClick={() => set({ defaultTeams: accounts.defaultTeams.filter((_, at) => at !== index) })}
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small" startIcon={<AddIcon />} sx={{ ...buttonStyles, mt: 1 }}
                onClick={() => freeTeam && set({
                  defaultTeams: [...accounts.defaultTeams, { teamId: freeTeam._id, role: 'INSPECTOR' }]
                })}
                disabled={disabled || !freeTeam}
              >
                {t('Customization_AddTeam')}
              </Button>
            </Box>
          </>
        )}

        {accounts.assignment === 'mapping' && (
          <>
            <TextField
              fullWidth size="small"
              label={t('ServerConfig_Sso_MappingClaim')}
              error={Boolean(fieldError('accounts.mappingClaim'))}
              helperText={fieldError('accounts.mappingClaim') ?? t('ServerConfig_Sso_MappingClaim_Description')}
              value={accounts.mappingClaim}
              onChange={(event) => set({ mappingClaim: event.target.value })}
              disabled={disabled}
            />

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('ServerConfig_Sso_Rules')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ServerConfig_Sso_Rules_Description')}
              </Typography>
              {fieldError('accounts.mappings') && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  {fieldError('accounts.mappings')}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {accounts.mappings.map((rule, index) => (
                  <Stack
                    // By position: a rule has no id, and its value is being typed.
                    key={index}
                    spacing={2}
                    sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                  >
                    {/* Numbered, since the order is what decides between two rules that hold. */}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                        {t('ServerConfig_Sso_Rule_Title', [index + 1])}
                      </Typography>
                      <IconButton
                        size="small" aria-label={t('Customization_Designer_MoveUp')}
                        onClick={() => moveRule(index, index - 1)}
                        disabled={disabled || index === 0}
                      >
                        <MoveUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small" aria-label={t('Customization_Designer_MoveDown')}
                        onClick={() => moveRule(index, index + 1)}
                        disabled={disabled || index === accounts.mappings.length - 1}
                      >
                        <MoveDownIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('ServerConfig_Sso_Rule_Delete', [index + 1])}
                        onClick={() => set({ mappings: accounts.mappings.filter((_, at) => at !== index) })}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {/* Two rows, and the two team fields share the second evenly: "role in team" is
                          a long label in French and German, and a narrow field cut it short. */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small" sx={{ flex: 1, minWidth: 0 }}
                        label={t('ServerConfig_Sso_Rule_Value')}
                        error={Boolean(fieldError(`accounts.mappings.${index}.value`))}
                        helperText={fieldError(`accounts.mappings.${index}.value`)}
                        value={rule.value}
                        onChange={(event) => setRule(index, { value: event.target.value })}
                        disabled={disabled}
                      />
                      {/* Wide enough for "Amministratore" and for "gives no role" in every language. */}
                      <TextField
                        select size="small" sx={{ width: 180, flexShrink: 0 }}
                        label={t('Common_Role')}
                        value={rule.role ?? ''}
                        error={Boolean(fieldError(`accounts.mappings.${index}.role`))}
                        helperText={fieldError(`accounts.mappings.${index}.role`)}
                        onChange={(event) => setRule(index, { role: (event.target.value || undefined) as SsoMappingRule['role'] })}
                        disabled={disabled}
                      >
                        <MenuItem value="">{t('ServerConfig_Sso_Rule_Unchanged')}</MenuItem>
                        {ssoAssignableRoles.map((role) => <MenuItem key={role} value={role}>{roleName(role)}</MenuItem>)}
                      </TextField>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select size="small" sx={{ flex: 1, minWidth: 0 }}
                        label={t('ServerConfig_Sso_Rule_Team')}
                        error={Boolean(fieldError(`accounts.mappings.${index}.teamId`))}
                        helperText={fieldError(`accounts.mappings.${index}.teamId`)}
                        value={rule.teamId ?? ''}
                        // A team and the role in it go together: the server takes neither alone.
                        onChange={(event) => setRule(index, event.target.value
                          ? { teamId: event.target.value, teamRole: rule.teamRole ?? 'INSPECTOR' }
                          : { teamId: undefined, teamRole: undefined })}
                        disabled={disabled}
                      >
                        <MenuItem value="">{t('ServerConfig_Sso_Rule_NoTeam')}</MenuItem>
                        {teamOptions(rule.teamId)}
                      </TextField>
                      <TextField
                        select size="small" sx={{ flex: 1, minWidth: 0 }}
                        label={t('ServerConfig_Sso_Rule_TeamRole')}
                        error={Boolean(fieldError(`accounts.mappings.${index}.teamRole`))}
                        helperText={fieldError(`accounts.mappings.${index}.teamRole`)}
                        value={rule.teamRole ?? ''}
                        onChange={(event) => setRule(index, { teamRole: event.target.value as TeamRole })}
                        disabled={disabled || !rule.teamId}
                      >
                        {teamRoles.map((role) => <MenuItem key={role} value={role}>{teamRoleName(role)}</MenuItem>)}
                      </TextField>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small" startIcon={<AddIcon />} sx={{ ...buttonStyles, mt: 1 }}
                onClick={() => set({ mappings: [...accounts.mappings, { value: '', role: 'user' }] })}
                disabled={disabled}
              >
                {t('Customization_AddRule')}
              </Button>
            </Box>

            {accounts.autoCreate && (
              <TextField
                select fullWidth size="small"
                label={t('ServerConfig_Sso_NewAccounts')}
                helperText={t(accounts.newAccountsManagedBy === 'sso'
                  ? 'ServerConfig_Sso_NewAccounts_Sso_Description'
                  : 'ServerConfig_Sso_NewAccounts_Local_Description')}
                value={accounts.newAccountsManagedBy}
                onChange={(event) => set({
                  newAccountsManagedBy: event.target.value as SsoAccounts['newAccountsManagedBy']
                })}
                disabled={disabled}
              >
                <MenuItem value="sso">{t('ServerConfig_Sso_NewAccounts_Sso')}</MenuItem>
                <MenuItem value="local">{t('ServerConfig_Sso_NewAccounts_Local')}</MenuItem>
              </TextField>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default SsoAccountsForm;
